import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fmtNum, fmtUSD, todayISO } from "../lib/api";
import TopBar from "../components/TopBar";
import { Modal, Field } from "./Fields";
import { toast } from "sonner";
import {
  ArrowLeft, Beaker, FlaskConical, Sprout, Users, Plus, Pencil, Trash2,
  Droplets, Package, TrendingUp,
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from "recharts";

/** Field-level agronomy hub: field basics, soil samples timeline, and
 *  fertilizer/manure application log. Reached from any field card via /fields/:id. */
export default function FieldDetail() {
  const { id } = useParams();
  const [field, setField] = useState(null);
  const [partners, setPartners] = useState([]);
  const [soil, setSoil] = useState([]);
  const [apps, setApps] = useState([]);
  const [soilOpen, setSoilOpen] = useState(false);
  const [appOpen, setAppOpen] = useState(false);
  const [npkOpen, setNpkOpen] = useState(false);
  const [editingSoil, setEditingSoil] = useState(null);
  const [editingApp, setEditingApp] = useState(null);

  const load = async () => {
    const [f, p, s, a] = await Promise.all([
      api.get(`/fields/${id}`),
      api.get("/partners"),
      api.get(`/soil-samples?field_id=${id}`),
      api.get(`/field-applications?field_id=${id}`),
    ]);
    setField(f.data);
    setPartners(p.data);
    setSoil(s.data);
    setApps(a.data);
  };
  useEffect(() => { load(); }, [id]);

  if (!field) {
    return <div className="p-8 text-[#66645E]">Loading field…</div>;
  }
  const partner = partners.find((x) => x.id === field.partner_id);
  const latest = soil[0];

  return (
    <div>
      <div className="mb-4">
        <Link to="/fields" data-testid="back-to-fields" className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#66645E] hover:text-[#1F1E1B]">
          <ArrowLeft className="w-3 h-3" /> All Fields
        </Link>
      </div>

      <TopBar
        title={field.name}
        subtitle={field.location || `${fmtNum(field.acreage, 1)} acres · ${field.crop_type}`}
      />

      {/* Field basics strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="field-detail-stats">
        <Stat label="Acres" value={fmtNum(field.acreage, 2)} />
        <Stat label="CSR / CSR2" value={field.csr ? fmtNum(field.csr, 1) : "—"} testid="field-detail-csr" />
        <Stat label="Plots" value={(field.plots || []).length || 1} />
        <Stat
          label="Partner"
          value={partner ? `${partner.name} · ${fmtNum(field.partner_split_pct, 1)}%` : "—"}
          testid="field-detail-partner"
        />
      </div>

      {/* Plots quick view */}
      {(field.plots || []).length > 0 && (
        <Section title="Plots (crops on this field)" icon={Sprout}>
          <div className="flex flex-wrap gap-2">
            {field.plots.map((p, i) => (
              <div key={p.id || i} className="border border-[#1F1E1B] bg-white px-3 py-2 text-sm" data-testid={`fd-plot-${i}`}>
                <span className="stamp stamp-green mr-2">{p.crop}</span>
                <span className="tabular font-bold">{fmtNum(p.acres, 1)}</span> <span className="text-[#66645E]">ac</span>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Soil Samples */}
      <Section
        title="Soil Samples"
        icon={FlaskConical}
        action={
          <button
            data-testid="add-soil-sample-btn"
            onClick={() => { setEditingSoil(null); setSoilOpen(true); }}
            className="bg-[#5C4A2B] text-white px-4 py-1.5 text-xs font-bold uppercase tracking-wider hover:bg-[#3F331D] transition-colors flex items-center gap-1.5"
          >
            <Plus className="w-3 h-3" /> Add Sample
          </button>
        }
      >
        {latest && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-4 p-3 border-2 border-[#5C4A2B] bg-[#FAF7EC]" data-testid="latest-soil-panel">
            <MiniStat label="Latest date" value={latest.date} />
            <MiniStat label="pH" value={latest.ph ?? "—"} />
            <MiniStat label="P (ppm)" value={latest.phosphorus_ppm ?? "—"} />
            <MiniStat label="K (ppm)" value={latest.potassium_ppm ?? "—"} />
            <MiniStat label="OM (%)" value={latest.organic_matter_pct ?? "—"} />
            <MiniStat label="N (ppm)" value={latest.nitrogen_ppm ?? "—"} />
          </div>
        )}
        {soil.length >= 2 && <SoilTrendChart samples={soil} />}
        {soil.length === 0 ? (
          <div className="text-sm text-[#66645E] italic p-4 border border-dashed border-[#E5E2D6] text-center" data-testid="soil-empty">
            No soil samples logged yet. Add one to start tracking pH, P, K, N, and organic matter over time.
          </div>
        ) : (
          <div className="bg-white border border-[#1F1E1B] overflow-x-auto">
            <table className="ledger-table" data-testid="soil-table">
              <thead>
                <tr>
                  <th>Date</th><th>Lab</th><th>Zone</th>
                  <th className="num">pH</th><th className="num">P (ppm)</th><th className="num">K (ppm)</th>
                  <th className="num">N (ppm)</th><th className="num">OM (%)</th><th className="num">CEC</th>
                  <th style={{ width: 80 }}></th>
                </tr>
              </thead>
              <tbody>
                {soil.map((s) => (
                  <tr key={s.id} data-testid={`soil-row-${s.id}`}>
                    <td className="tabular">{s.date}</td>
                    <td>{s.lab_name || "—"}</td>
                    <td className="text-xs text-[#66645E]">{s.zone || "—"}</td>
                    <td className="num">{s.ph ?? "—"}</td>
                    <td className="num">{s.phosphorus_ppm ?? "—"}</td>
                    <td className="num">{s.potassium_ppm ?? "—"}</td>
                    <td className="num">{s.nitrogen_ppm ?? "—"}</td>
                    <td className="num">{s.organic_matter_pct ?? "—"}</td>
                    <td className="num">{s.cec ?? "—"}</td>
                    <td>
                      <div className="flex gap-1 justify-end">
                        <button data-testid={`edit-soil-${s.id}`} onClick={() => { setEditingSoil(s); setSoilOpen(true); }} className="p-1 border border-[#1F1E1B] hover:bg-[#1F1E1B] hover:text-white"><Pencil className="w-3 h-3" /></button>
                        <button data-testid={`delete-soil-${s.id}`} onClick={async () => { if (window.confirm("Delete this soil sample?")) { await api.delete(`/soil-samples/${s.id}`); toast.success("Sample deleted"); load(); } }} className="p-1 border border-[#BC4727] text-[#BC4727] hover:bg-[#BC4727] hover:text-white"><Trash2 className="w-3 h-3" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* Applications (fertilizer + manure) */}
      <Section
        title="Fertilizer & Manure Applications"
        icon={Droplets}
        action={
          <div className="flex gap-2">
            <button
              data-testid="npk-budget-btn"
              onClick={() => setNpkOpen(true)}
              className="border border-[#1F1E1B] px-4 py-1.5 text-xs font-bold uppercase tracking-wider hover:bg-[#1F1E1B] hover:text-white transition-colors flex items-center gap-1.5"
            >
              <Beaker className="w-3 h-3" /> NPK Budget
            </button>
            <button
              data-testid="add-application-btn"
              onClick={() => { setEditingApp(null); setAppOpen(true); }}
              className="bg-[#1A4135] text-white px-4 py-1.5 text-xs font-bold uppercase tracking-wider hover:bg-[#133128] transition-colors flex items-center gap-1.5"
            >
              <Plus className="w-3 h-3" /> Log Application
            </button>
          </div>
        }
      >
        {apps.length === 0 ? (
          <div className="text-sm text-[#66645E] italic p-4 border border-dashed border-[#E5E2D6] text-center" data-testid="apps-empty">
            No fertilizer or manure applications yet. Log rate per acre for anything you spread on this field.
          </div>
        ) : (
          <div className="bg-white border border-[#1F1E1B] overflow-x-auto">
            <table className="ledger-table" data-testid="apps-table">
              <thead>
                <tr>
                  <th>Date</th><th>Kind</th><th>Product</th>
                  <th className="num">Rate</th><th className="num">Acres</th>
                  <th>NPK</th><th>Notes</th>
                  <th style={{ width: 80 }}></th>
                </tr>
              </thead>
              <tbody>
                {apps.map((a) => {
                  const npk = [a.n_pct, a.p_pct, a.k_pct].map((v) => v ?? "—").join(" · ");
                  return (
                    <tr key={a.id} data-testid={`app-row-${a.id}`}>
                      <td className="tabular">{a.date}</td>
                      <td>
                        <span className={`stamp text-[10px] ${a.kind === "manure" ? "" : "stamp-green"}`} style={a.kind === "manure" ? { background: "#5C4A2B", color: "white", borderColor: "#5C4A2B" } : undefined}>
                          {a.kind === "manure" ? "Manure" : "Commercial"}
                        </span>
                      </td>
                      <td className="font-semibold">{a.product}</td>
                      <td className="num tabular">{fmtNum(a.rate_value, 2)} <span className="text-[10px] text-[#66645E]">{a.rate_unit}</span></td>
                      <td className="num tabular">{fmtNum(a.acres_covered, 1)}</td>
                      <td className="text-xs text-[#66645E]">{npk === "— · — · —" ? "—" : npk}</td>
                      <td className="text-xs text-[#66645E] max-w-[200px] truncate">{a.notes}</td>
                      <td>
                        <div className="flex gap-1 justify-end">
                          <button data-testid={`edit-app-${a.id}`} onClick={() => { setEditingApp(a); setAppOpen(true); }} className="p-1 border border-[#1F1E1B] hover:bg-[#1F1E1B] hover:text-white"><Pencil className="w-3 h-3" /></button>
                          <button data-testid={`delete-app-${a.id}`} onClick={async () => { if (window.confirm("Delete this application?")) { await api.delete(`/field-applications/${a.id}`); toast.success("Application deleted"); load(); } }} className="p-1 border border-[#BC4727] text-[#BC4727] hover:bg-[#BC4727] hover:text-white"><Trash2 className="w-3 h-3" /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {soilOpen && (
        <SoilSampleModal
          field={field}
          initial={editingSoil}
          onClose={() => { setSoilOpen(false); setEditingSoil(null); }}
          onSaved={() => { setSoilOpen(false); setEditingSoil(null); load(); }}
        />
      )}
      {appOpen && (
        <ApplicationModal
          field={field}
          initial={editingApp}
          onClose={() => { setAppOpen(false); setEditingApp(null); }}
          onSaved={() => { setAppOpen(false); setEditingApp(null); load(); }}
        />
      )}
      {npkOpen && (
        <NPKBudgetModal field={field} onClose={() => setNpkOpen(false)} />
      )}
    </div>
  );
}

function Section({ title, icon: Icon, action, children }) {
  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-lg font-black flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-[#5C4A2B]" />} {title}
        </h2>
        {action}
      </div>
      {children}
    </div>
  );
}
function Stat({ label, value, testid }) {
  return (
    <div className="border border-[#1F1E1B] bg-white p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.18em] text-[#66645E]">{label}</div>
      <div className="font-display text-2xl font-black tabular mt-1">{value}</div>
    </div>
  );
}
function MiniStat({ label, value }) {
  return (
    <div className="text-center">
      <div className="text-[9px] uppercase tracking-wider text-[#66645E]">{label}</div>
      <div className="tabular font-bold">{value}</div>
    </div>
  );
}

// ------- Soil Sample Modal -------
const blankSoil = {
  date: todayISO(), lab_name: "", ph: "", phosphorus_ppm: "", potassium_ppm: "",
  nitrogen_ppm: "", organic_matter_pct: "", cec: "", depth_inches: 6, zone: "", notes: "",
};

function SoilSampleModal({ field, initial, onClose, onSaved }) {
  const [form, setForm] = useState(() => {
    if (!initial) return blankSoil;
    return {
      date: initial.date, lab_name: initial.lab_name || "",
      ph: initial.ph ?? "", phosphorus_ppm: initial.phosphorus_ppm ?? "",
      potassium_ppm: initial.potassium_ppm ?? "", nitrogen_ppm: initial.nitrogen_ppm ?? "",
      organic_matter_pct: initial.organic_matter_pct ?? "", cec: initial.cec ?? "",
      depth_inches: initial.depth_inches ?? 6, zone: initial.zone || "",
      notes: initial.notes || "",
    };
  });

  const onSubmit = async (e) => {
    e.preventDefault();
    const num = (v) => v === "" || v === null || v === undefined ? null : parseFloat(v);
    const payload = {
      field_id: field.id,
      date: form.date,
      lab_name: form.lab_name,
      ph: num(form.ph),
      phosphorus_ppm: num(form.phosphorus_ppm),
      potassium_ppm: num(form.potassium_ppm),
      nitrogen_ppm: num(form.nitrogen_ppm),
      organic_matter_pct: num(form.organic_matter_pct),
      cec: num(form.cec),
      depth_inches: num(form.depth_inches) ?? 6,
      zone: form.zone,
      notes: form.notes,
    };
    try {
      if (initial) {
        await api.put(`/soil-samples/${initial.id}`, payload);
        toast.success("Sample updated");
      } else {
        await api.post("/soil-samples", payload);
        toast.success("Sample added");
      }
      onSaved();
    } catch {
      toast.error("Could not save sample");
    }
  };

  return (
    <Modal title={initial ? "Edit Soil Sample" : "New Soil Sample"} onClose={onClose}>
      <form onSubmit={onSubmit} className="space-y-4" data-testid="soil-form">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Sample Date" required>
            <input data-testid="soil-date-input" type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular" />
          </Field>
          <Field label="Lab (optional)">
            <input data-testid="soil-lab-input" value={form.lab_name} onChange={(e) => setForm({ ...form, lab_name: e.target.value })} placeholder="e.g. AgSource, Midwest Labs" className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
          </Field>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <NumField label="pH" testid="soil-ph-input" value={form.ph} onChange={(v) => setForm({ ...form, ph: v })} step="0.1" placeholder="6.8" />
          <NumField label="P (ppm)" testid="soil-p-input" value={form.phosphorus_ppm} onChange={(v) => setForm({ ...form, phosphorus_ppm: v })} placeholder="25" />
          <NumField label="K (ppm)" testid="soil-k-input" value={form.potassium_ppm} onChange={(v) => setForm({ ...form, potassium_ppm: v })} placeholder="180" />
          <NumField label="N (ppm)" testid="soil-n-input" value={form.nitrogen_ppm} onChange={(v) => setForm({ ...form, nitrogen_ppm: v })} placeholder="12" />
          <NumField label="Organic Matter (%)" testid="soil-om-input" value={form.organic_matter_pct} onChange={(v) => setForm({ ...form, organic_matter_pct: v })} step="0.1" placeholder="4.2" />
          <NumField label="CEC" testid="soil-cec-input" value={form.cec} onChange={(v) => setForm({ ...form, cec: v })} step="0.1" placeholder="18" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <NumField label="Depth (in)" testid="soil-depth-input" value={form.depth_inches} onChange={(v) => setForm({ ...form, depth_inches: v })} step="0.5" placeholder="6" />
          <Field label="Zone / Location (optional)">
            <input data-testid="soil-zone-input" value={form.zone} onChange={(e) => setForm({ ...form, zone: e.target.value })} placeholder="e.g. North half, Grid A1" className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
          </Field>
        </div>
        <Field label="Notes">
          <textarea data-testid="soil-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="border border-[#1F1E1B] px-5 py-2 text-sm font-bold uppercase tracking-wider">Cancel</button>
          <button type="submit" data-testid="soil-submit-btn" className="bg-[#5C4A2B] text-white px-5 py-2 text-sm font-bold uppercase tracking-wider hover:bg-[#3F331D]">Save Sample</button>
        </div>
      </form>
    </Modal>
  );
}

// ------- Application Modal -------
const RATE_UNITS = ["lb/ac", "ton/ac", "gal/ac", "kg/ac", "cwt/ac"];
const COMMON_COMMERCIAL = ["Urea (46-0-0)", "Anhydrous Ammonia (82-0-0)", "DAP (18-46-0)", "MAP (11-52-0)", "Potash (0-0-60)", "10-34-0", "28-0-0-5S UAN", "Lime"];
const COMMON_MANURE = ["Hog Manure (liquid)", "Cattle Manure (solid)", "Poultry Litter", "Dairy Manure", "Compost"];

const blankApp = {
  date: todayISO(), kind: "commercial", product: "Urea (46-0-0)",
  rate_value: "", rate_unit: "lb/ac", acres_covered: "",
  n_pct: "", p_pct: "", k_pct: "", plot_id: "", notes: "",
  total_cost: "",
};

function ApplicationModal({ field, initial, onClose, onSaved }) {
  const [form, setForm] = useState(() => {
    if (!initial) return { ...blankApp, acres_covered: field.acreage || "" };
    return {
      date: initial.date, kind: initial.kind, product: initial.product,
      rate_value: initial.rate_value ?? "", rate_unit: initial.rate_unit || "lb/ac",
      acres_covered: initial.acres_covered ?? "",
      n_pct: initial.n_pct ?? "", p_pct: initial.p_pct ?? "", k_pct: initial.k_pct ?? "",
      plot_id: initial.plot_id || "", notes: initial.notes || "",
      total_cost: "",
    };
  });

  // If editing an app already linked to an expense, pull the current cost so
  // the "Total Cost" input shows what's on the ledger (kept in sync on save).
  useEffect(() => {
    if (!initial?.expense_id) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/expenses?year=${(initial.date || "").slice(0, 4)}`);
        const linked = (data || []).find((e) => e.id === initial.expense_id);
        if (!cancelled && linked) {
          setForm((prev) => ({ ...prev, total_cost: linked.amount ?? "" }));
        }
      } catch { /* silent — cost input just stays blank */ }
    })();
    return () => { cancelled = true; };
  }, [initial]);


  const productOptions = form.kind === "manure" ? COMMON_MANURE : COMMON_COMMERCIAL;
  const rateHint = useMemo(() => {
    if (!form.rate_value || !form.acres_covered) return null;
    const rate = parseFloat(form.rate_value) || 0;
    const acres = parseFloat(form.acres_covered) || 0;
    if (!rate || !acres) return null;
    const total = rate * acres;
    const unit = form.rate_unit.split("/")[0];  // "lb"
    return `≈ ${fmtNum(total, 1)} ${unit} across ${fmtNum(acres, 1)} ac`;
  }, [form.rate_value, form.acres_covered, form.rate_unit]);

  const onSubmit = async (e) => {
    e.preventDefault();
    const num = (v) => v === "" || v === null || v === undefined ? null : parseFloat(v);
    const payload = {
      field_id: field.id,
      plot_id: form.plot_id || "",
      date: form.date,
      kind: form.kind,
      product: form.product,
      rate_value: parseFloat(form.rate_value) || 0,
      rate_unit: form.rate_unit,
      acres_covered: parseFloat(form.acres_covered) || 0,
      n_pct: num(form.n_pct),
      p_pct: num(form.p_pct),
      k_pct: num(form.k_pct),
      notes: form.notes,
      total_cost: parseFloat(form.total_cost) || 0,
    };
    try {
      if (initial) {
        await api.put(`/field-applications/${initial.id}`, payload);
        toast.success("Application updated");
      } else {
        await api.post("/field-applications", payload);
        toast.success("Application logged");
      }
      onSaved();
    } catch {
      toast.error("Could not save application");
    }
  };

  const plots = field.plots || [];

  return (
    <Modal title={initial ? "Edit Application" : "Log Fertilizer / Manure Application"} onClose={onClose}>
      <form onSubmit={onSubmit} className="space-y-4" data-testid="application-form">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Date" required>
            <input data-testid="app-date-input" type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular" />
          </Field>
          <Field label="Kind" required>
            <div className="grid grid-cols-2 gap-1">
              <button type="button" data-testid="app-kind-commercial" onClick={() => setForm({ ...form, kind: "commercial", product: COMMON_COMMERCIAL[0] })}
                className={`px-3 py-2 text-xs font-bold uppercase tracking-wider border ${form.kind === "commercial" ? "bg-[#1A4135] text-white border-[#1A4135]" : "border-[#1F1E1B]"}`}>Commercial</button>
              <button type="button" data-testid="app-kind-manure" onClick={() => setForm({ ...form, kind: "manure", product: COMMON_MANURE[0], rate_unit: "gal/ac" })}
                className={`px-3 py-2 text-xs font-bold uppercase tracking-wider border ${form.kind === "manure" ? "bg-[#5C4A2B] text-white border-[#5C4A2B]" : "border-[#1F1E1B]"}`}>Manure</button>
            </div>
          </Field>
        </div>
        <Field label="Product" required>
          <input
            data-testid="app-product-input"
            list={`app-product-list-${form.kind}`}
            required
            value={form.product}
            onChange={(e) => setForm({ ...form, product: e.target.value })}
            placeholder={form.kind === "manure" ? "e.g. Hog Manure (liquid)" : "e.g. Urea (46-0-0)"}
            className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm"
          />
          <datalist id={`app-product-list-${form.kind}`}>
            {productOptions.map((p) => <option key={p} value={p} />)}
          </datalist>
        </Field>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Rate Value" required>
            <input data-testid="app-rate-input" type="number" step="0.01" required value={form.rate_value} onChange={(e) => setForm({ ...form, rate_value: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular" />
          </Field>
          <Field label="Rate Unit">
            <select data-testid="app-unit-select" value={form.rate_unit} onChange={(e) => setForm({ ...form, rate_unit: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm">
              {RATE_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          </Field>
          <Field label="Acres Covered">
            <input data-testid="app-acres-input" type="number" step="0.01" value={form.acres_covered} onChange={(e) => setForm({ ...form, acres_covered: e.target.value })} placeholder={`${field.acreage} (whole field)`} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular" />
          </Field>
        </div>
        {rateHint && (
          <div className="text-[11px] text-[#66645E] bg-[#FAF7EC] border border-[#E5E2D6] px-3 py-1.5" data-testid="app-rate-hint">
            {rateHint}
          </div>
        )}
        {plots.length >= 2 && (
          <Field label="Plot (optional)">
            <select data-testid="app-plot-select" value={form.plot_id} onChange={(e) => setForm({ ...form, plot_id: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm">
              <option value="">— Whole field —</option>
              {plots.map((p) => <option key={p.id} value={p.id}>{p.crop} · {p.acres} ac</option>)}
            </select>
          </Field>
        )}
        <div className="border border-[#E5E2D6] bg-[#FAF7EC] p-3">
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E] mb-2">NPK Analysis (optional — off the bag or manure test)</div>
          <div className="grid grid-cols-3 gap-3">
            <NumField label="N %" testid="app-n-input" value={form.n_pct} onChange={(v) => setForm({ ...form, n_pct: v })} step="0.1" placeholder="46" />
            <NumField label="P %" testid="app-p-input" value={form.p_pct} onChange={(v) => setForm({ ...form, p_pct: v })} step="0.1" placeholder="0" />
            <NumField label="K %" testid="app-k-input" value={form.k_pct} onChange={(v) => setForm({ ...form, k_pct: v })} step="0.1" placeholder="0" />
          </div>
        </div>
        <div className="border-2 border-[#1A4135] bg-[#F5F4F0] p-3" data-testid="app-total-cost-section">
          <div className="flex items-baseline justify-between mb-1">
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#1A4135]">Total Cost (optional)</div>
            <div className="text-[10px] text-[#66645E]">auto-creates matching expense</div>
          </div>
          <NumField
            label="What did this application cost you, all-in?"
            testid="app-total-cost-input"
            value={form.total_cost}
            onChange={(v) => setForm({ ...form, total_cost: v })}
            step="0.01"
            placeholder="e.g. 2400.00"
          />
          <div className="text-[11px] text-[#66645E] mt-1">
            Enter a total and we&apos;ll add a matching row to your Expense ledger (linked back to this application). Leave blank to log the application without a cost entry.
          </div>
        </div>
        <Field label="Notes">
          <textarea data-testid="app-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="border border-[#1F1E1B] px-5 py-2 text-sm font-bold uppercase tracking-wider">Cancel</button>
          <button type="submit" data-testid="app-submit-btn" className="bg-[#1A4135] text-white px-5 py-2 text-sm font-bold uppercase tracking-wider hover:bg-[#133128]">Save Application</button>
        </div>
      </form>
    </Modal>
  );
}

function NumField({ label, value, onChange, step = "0.01", placeholder, testid }) {
  return (
    <Field label={label}>
      <input
        data-testid={testid}
        type="number"
        step={step}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular"
      />
    </Field>
  );
}

// ------- Soil Trend Chart -------
function SoilTrendChart({ samples }) {
  // Chronological (oldest → newest) so the chart reads left-to-right through time
  const data = [...samples].reverse().map((s) => ({
    date: s.date,
    pH: s.ph ?? null,
    "P (ppm)": s.phosphorus_ppm ?? null,
    "K (ppm)": s.potassium_ppm ?? null,
    "OM (%)": s.organic_matter_pct ?? null,
  }));
  return (
    <div className="border-2 border-[#5C4A2B] bg-white mb-4 p-3" data-testid="soil-trend-chart">
      <div className="flex items-center gap-2 mb-2">
        <TrendingUp className="w-4 h-4 text-[#5C4A2B]" />
        <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#66645E]">
          Fertility Trend
        </span>
      </div>
      <ResponsiveContainer width="100%" height={220}>
        <LineChart data={data} margin={{ top: 4, right: 12, left: 0, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E2D6" />
          <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#66645E" }} />
          <YAxis tick={{ fontSize: 11, fill: "#66645E" }} />
          <Tooltip contentStyle={{ fontSize: 12, border: "1px solid #1F1E1B" }} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Line type="monotone" dataKey="pH" stroke="#1A4135" strokeWidth={2} dot={{ r: 3 }} connectNulls />
          <Line type="monotone" dataKey="P (ppm)" stroke="#BC4727" strokeWidth={2} dot={{ r: 3 }} connectNulls />
          <Line type="monotone" dataKey="K (ppm)" stroke="#5C4A2B" strokeWidth={2} dot={{ r: 3 }} connectNulls />
          <Line type="monotone" dataKey="OM (%)" stroke="#E5A835" strokeWidth={2} dot={{ r: 3 }} connectNulls />
        </LineChart>
      </ResponsiveContainer>
      <div className="text-[10px] text-[#66645E] italic mt-1">
        Different scales share one axis — use the tooltip to inspect exact values. Add more samples to sharpen the trend.
      </div>
    </div>
  );
}


// ------- NPK Budget Modal -------
const CROP_DEFAULT_YIELD = { Corn: 200, Soybeans: 60, Wheat: 70, Oats: 90, Alfalfa: 5, Hay: 4, Barley: 80, "Grain Sorghum": 90 };

function NPKBudgetModal({ field, onClose }) {
  const [crops, setCrops] = useState([]);
  const [crop, setCrop] = useState((field.plots?.[0]?.crop) || field.crop_type || "Corn");
  const [yieldVal, setYieldVal] = useState(CROP_DEFAULT_YIELD[crop] || 100);
  const [manureN, setManureN] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/agronomy/removal-rates").then((r) => setCrops(Object.keys(r.data))).catch(() => setCrops(["Corn", "Soybeans", "Wheat", "Oats", "Alfalfa", "Hay"]));
  }, []);

  useEffect(() => {
    // Auto-pick a sensible default yield when the user changes crops
    if (CROP_DEFAULT_YIELD[crop]) setYieldVal(CROP_DEFAULT_YIELD[crop]);
  }, [crop]);

  const compute = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/agronomy/npk-recommendation", {
        field_id: field.id,
        crop,
        target_yield: parseFloat(yieldVal) || 0,
        manure_n_credit: parseFloat(manureN) || 0,
      });
      setResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not compute recommendation");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal title="NPK Budget Calculator" onClose={onClose}>
      <div className="space-y-4" data-testid="npk-budget-modal">
        <div className="grid grid-cols-3 gap-3">
          <Field label="Crop" required>
            <select data-testid="npk-crop-select" value={crop} onChange={(e) => setCrop(e.target.value)} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm">
              {crops.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Target Yield" required>
            <input data-testid="npk-yield-input" type="number" step="0.1" value={yieldVal} onChange={(e) => setYieldVal(e.target.value)} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular" />
          </Field>
          <Field label="Manure N Credit (lb/ac, optional)">
            <input data-testid="npk-manure-input" type="number" step="0.1" value={manureN} onChange={(e) => setManureN(e.target.value)} placeholder="0" className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular" />
          </Field>
        </div>
        <button
          type="button"
          data-testid="npk-compute-btn"
          onClick={compute}
          disabled={loading}
          className="w-full bg-[#1A4135] text-white py-2 text-sm font-bold uppercase tracking-wider hover:bg-[#133128] disabled:opacity-40"
        >
          {loading ? "Computing…" : "Compute Recommendation →"}
        </button>

        {result && (
          <div className="border-2 border-[#1F1E1B] bg-[#FAF7EC] p-3 space-y-3" data-testid="npk-result-panel">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#66645E]">
              Recommendation · {result.crop} @ {result.target_yield} {result.target_yield_unit}/ac
            </div>
            <div className="grid grid-cols-3 gap-2">
              <NpkCard label="N" value={result.recommendation_lb_per_ac.n} note={result.notes.n} accent="#1A4135" testid="npk-n" />
              <NpkCard label="P₂O₅" value={result.recommendation_lb_per_ac.p2o5} note={result.notes.p2o5} accent="#BC4727" testid="npk-p" />
              <NpkCard label="K₂O" value={result.recommendation_lb_per_ac.k2o} note={result.notes.k2o} accent="#5C4A2B" testid="npk-k" />
            </div>
            <div className="grid grid-cols-3 gap-2 text-center text-[11px] text-[#66645E] pt-2 border-t border-[#E5E2D6]">
              <div>Removal: <span className="tabular font-bold">{result.removal_lb_per_ac.n} lb N</span></div>
              <div>Removal: <span className="tabular font-bold">{result.removal_lb_per_ac.p2o5} lb P₂O₅</span></div>
              <div>Removal: <span className="tabular font-bold">{result.removal_lb_per_ac.k2o} lb K₂O</span></div>
            </div>
            {result.soil_reference && (
              <div className="bg-white border border-[#E5E2D6] p-2 text-xs" data-testid="npk-soil-ref">
                <div className="text-[10px] font-bold uppercase tracking-wider text-[#66645E] mb-1">Latest soil sample ({result.soil_reference.date})</div>
                pH {result.soil_reference.ph ?? "—"} · P {result.soil_reference.phosphorus_ppm ?? "—"} ppm · K {result.soil_reference.potassium_ppm ?? "—"} ppm · OM {result.soil_reference.organic_matter_pct ?? "—"}%
              </div>
            )}
            {result.warnings.length > 0 && (
              <ul className="text-xs bg-[#FDF5D5] border border-[#B87A00] p-2 list-disc list-inside" data-testid="npk-warnings">
                {result.warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            )}
            <div className="text-[10px] uppercase tracking-wider text-[#66645E] italic pt-1 border-t border-[#E5E2D6]">
              Rule of thumb only — cross-check with your ag agent, especially on unfamiliar soil types.
            </div>
          </div>
        )}

        <div className="flex justify-end pt-2">
          <button type="button" onClick={onClose} className="border border-[#1F1E1B] px-5 py-2 text-sm font-bold uppercase tracking-wider">Close</button>
        </div>
      </div>
    </Modal>
  );
}

function NpkCard({ label, value, note, accent, testid }) {
  return (
    <div className="bg-white border border-[#1F1E1B] p-3" data-testid={testid} style={{ borderTop: `4px solid ${accent}` }}>
      <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#66645E]">{label}</div>
      <div className="font-display text-3xl font-black tabular" style={{ color: accent }}>{value}</div>
      <div className="text-[9px] uppercase tracking-wider text-[#66645E]">lb / ac</div>
      <div className="text-[10px] text-[#66645E] mt-1 leading-tight">{note}</div>
    </div>
  );
}

